// Server-rendered Codex concept reads.
//
// The Codex is a public, indexable part of the repository, so a concept page
// must render its real text on the server rather than fetching after
// hydration. Reads go through the publishable key under the public SELECT
// policies on codex_* — no privileged access, no inferred relationships.

import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { z } from "zod";
import type { Database } from "@/integrations/supabase/types";
import type { CodexConcept, CodexLink } from "@/lib/codex";

function publicClient() {
  const key = process.env.SUPABASE_PUBLISHABLE_KEY!;
  return createClient<Database>(process.env.SUPABASE_URL!, key, {
    auth: { persistSession: false, autoRefreshToken: false },
    global: {
      fetch: (input, init) => {
        const h = new Headers(init?.headers);
        if (key.startsWith("sb_") && h.get("Authorization") === `Bearer ${key}`) h.delete("Authorization");
        h.set("apikey", key);
        return fetch(input, { ...init, headers: h });
      },
    },
  });
}

export type CodexRelated = { slug: string; title: string; essence: string | null };

export type CodexConceptPage = {
  concept: CodexConcept;
  related: CodexRelated[];
  links: CodexLink[];
};

const CONCEPT_COLS = "slug, title, essence, definition, cluster, sort_order";

export const getCodexConceptPage = createServerFn({ method: "GET" })
  .inputValidator((d: { slug: string }) => z.object({ slug: z.string().min(1).max(120) }).parse(d))
  .handler(async ({ data }): Promise<CodexConceptPage | null> => {
    const supabase = publicClient();

    const { data: concept, error } = await supabase
      .from("codex_concepts")
      .select(CONCEPT_COLS)
      .eq("slug", data.slug)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!concept) return null;

    const [relations, links] = await Promise.all([
      supabase
        .from("codex_relations")
        .select("from_slug, to_slug, kind, weight")
        .or(`from_slug.eq.${data.slug},to_slug.eq.${data.slug}`),
      supabase
        .from("codex_links")
        .select("concept_slug, entity_kind, entity_ref, label, weight")
        .eq("concept_slug", data.slug)
        .order("weight", { ascending: false }),
    ]);
    if (relations.error) throw new Error(relations.error.message);
    if (links.error) throw new Error(links.error.message);

    const relatedSlugs = Array.from(
      new Set(
        (relations.data ?? []).map((r) => (r.from_slug === data.slug ? r.to_slug : r.from_slug)),
      ),
    ).filter((s) => s && s !== data.slug);

    let related: CodexRelated[] = [];
    if (relatedSlugs.length) {
      const { data: rows, error: relErr } = await supabase
        .from("codex_concepts")
        .select("slug, title, essence")
        .in("slug", relatedSlugs)
        .order("sort_order", { ascending: true });
      if (relErr) throw new Error(relErr.message);
      related = (rows ?? []) as CodexRelated[];
    }

    return {
      concept: concept as CodexConcept,
      related,
      links: (links.data ?? []) as CodexLink[],
    };
  });
